import { toNestError } from '@hookform/resolvers';
import {
  AA_SOURCE_SYSTEM,
  ADMIN_GROUP,
  AMS_SOURCE_SYSTEM,
  COX_MAIL_ERROR_MSG,
  COX_PHONE_ERROR_MSG,
} from 'constants/details.constants';
import { cloneDeep, forEach, isEmpty, some, groupBy } from 'lodash';
import { useCallback } from 'react';
import { appendErrors } from 'react-hook-form';
import 'schemas/yupHelper';
import { getRawPhoneNumber } from 'utils/formatters';
import * as yup from 'yup';
import { validateEmail, validatePhone } from './validation';

const PHONE_ERROR_MSG = 'Please enter a valid phone number';

yup.addMethod(
  yup.string,
  'isCoxEmailValid',
  function (propertyName, message = COX_MAIL_ERROR_MSG) {
    return this.test('isCoxEmailValid', message, (value) => {
      if (value?.length > 0) {
        return validateEmail(value) === true;
      }

      return false;
    });
  }
);

yup.addMethod(
  yup.string,
  'isCoxPhoneValid',
  function (propertyName, message = COX_PHONE_ERROR_MSG) {
    return this.test('isCoxPhoneValid', message, (value) => {
      if (value?.length > 0) {
        return validatePhone(value) === true;
      }

      return false;
    });
  }
);

yup.addMethod(
  yup.string,
  'isValidPhone',
  function (propertyName, message = PHONE_ERROR_MSG) {
    return this.test('isValidPhone', message, (value, context) => {
      const validateResult = validatePhone(value);
      if (validateResult === true) return true;

      return false;
    });
  }
);

export const findDuplicateAndDeletableContacts = (arr, name) => {
  let nonDeletedContacts = arr.filter(c => !c.deleted && c[name])
  let groupedNonDeletedContacts = groupBy(nonDeletedContacts, c => c[name].toString().toLowerCase())
  return Object.values(groupedNonDeletedContacts)
    .filter(contact_group => contact_group.length > 1)
    .flat(1)
    .filter(c => !c.isNonAMS)
};

const testDuplicateContacts = (arr, propertyName, message, createError) => {
  const duplicateResult = findDuplicateAndDeletableContacts(arr, propertyName);
  const errors = [];

  if (duplicateResult?.length > 0) {
    duplicateResult.forEach((duplicate) => {
      const { parentName, index } = duplicate;
      const path = `${parentName}.${index}.${propertyName}`;

      errors.push(
        createError({
          path,
          message,
        })
      );
    });
  }

  if (!isEmpty(errors)) {
    throw new yup.ValidationError(errors);
  }

  return true;
};

yup.addMethod(
  yup.object,
  'notDuplicateEmail',
  function (propertyName, message = 'Duplicated') {
    return this.test('unique', message, function () {
      return testDuplicateContacts(
        this.parent.map((v) => ({
          ...v,
          value: v?.value.toString().toLowerCase(),
        })),
        propertyName,
        message,
        this.createError
      );
    });
  }
);

yup.addMethod(
  yup.object,
  'notDuplicatePhone',
  function (propertyName, message = 'Duplicated') {
    return this.test('unique', message, function () {
      return testDuplicateContacts(
        this.parent.map((v) => ({
          ...v,
          value: getRawPhoneNumber(v?.value),
        })),
        propertyName,
        message,
        this.createError
      );
    });
  }
);
/**
 * Why `path!` ? because it could be `undefined` in some case
 * https://github.com/jquense/yup#validationerrorerrors-string--arraystring-value-any-path-string
 */
export const parseErrorSchema = (error, validateAllFieldCriteria) => {
  return error?.inner?.reduce((previous, error) => {
    if (error?.path && !previous[error?.path]) {
      previous[error?.path] = { message: error.message, type: error?.type };
    }

    if (validateAllFieldCriteria) {
      const types = previous[error?.path].types;
      const messages = types && types[error?.type];

      previous[error?.path] = appendErrors(
        error?.path,
        validateAllFieldCriteria,
        previous,
        error?.type,
        messages ? [].concat(messages, error.message) : error.message
      );
    }

    return previous;
  }, {});
};

export const useYupValidationResolver = (
  validationSchema,
  sourceSystem = '',
  groups
) => {
  return useCallback(
    async (data, context, options) => {
      const { setErrors } = context;
      try {
        let cloneData = forEach(cloneDeep(data || {}), (fields, name) => {
          if (fields && fields.forEach)
            fields?.forEach((value, index) => {
              value.parentName = name;
              value.index = index;
            });
        });
        if (
          sourceSystem === AA_SOURCE_SYSTEM ||
          (sourceSystem !== AMS_SOURCE_SYSTEM &&
            sourceSystem !== AA_SOURCE_SYSTEM &&
            !some(groups, (v) => ADMIN_GROUP === v))
        ) {
          delete cloneData?.firstName;
          delete cloneData?.middleName;
          delete cloneData?.lastName;
          delete validationSchema?.fields?.firstName;
          delete validationSchema?.fields?.middleName;
          delete validationSchema?.fields?.lastName;
        }
        const values = await validationSchema.validate(cloneData, {
          abortEarly: false,
        });

        setErrors({});
        return {
          values,
          errors: {},
        };
      } catch (errors) {
        const finalErrors = toNestError(
          parseErrorSchema(errors, false),
          options.fields
        );

        setErrors(finalErrors);
        return {
          values: {},
          errors: finalErrors,
        };
      }
    },
    [sourceSystem, validationSchema, groups]
  );
};
